import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, MapPin, Clock, Check, X, ChevronRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PackageDetailsPage() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchPkg = async () => {
      try {
        const res = await axios.get(`${API}/packages/${id}`);
        setPkg(res.data);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchPkg();
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white pt-20">
      <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!pkg) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white pt-20">
      <p className="font-body text-gray-400 mb-4">Package not found</p>
      <Link to="/explore" className="text-gold font-body hover:underline">Browse Packages</Link>
    </div>
  );

  return (
    <div data-testid="package-details-page" className="min-h-screen bg-white pt-20">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center gap-2 font-body text-sm text-gray-400">
          <Link to="/" className="hover:text-gold">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/explore" className="hover:text-gold">Packages</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-navy">{pkg.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left - Content */}
          <div className="lg:col-span-2">
            {/* Hero Image */}
            <div className="rounded-3xl overflow-hidden h-[400px] relative mb-8">
              <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 bg-navy/80 backdrop-blur-sm text-white text-xs font-body px-4 py-1.5 rounded-full capitalize">{pkg.category}</div>
            </div>

            <h1 data-testid="pkg-detail-name" className="font-heading text-3xl sm:text-4xl font-bold text-navy mb-3">{pkg.name}</h1>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <span className="flex items-center gap-1 font-body text-sm text-gray-500"><MapPin className="w-4 h-4 text-gold" />{pkg.destination}</span>
              <span className="flex items-center gap-1 font-body text-sm text-gray-500"><Clock className="w-4 h-4 text-gold" />{pkg.duration}</span>
              <span className="flex items-center gap-1 font-body text-sm text-gray-500"><Star className="w-4 h-4 fill-gold text-gold" />{pkg.rating} ({pkg.reviews_count} reviews)</span>
            </div>
            <p className="font-body text-base text-gray-600 leading-relaxed mb-8">{pkg.description}</p>

            {/* Highlights */}
            {pkg.highlights?.length > 0 && (
              <div className="mb-10">
                <h3 className="font-heading text-xl font-semibold text-navy mb-4">Highlights</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pkg.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-navy-50">
                      <div className="w-8 h-8 bg-gold/10 rounded-xl flex items-center justify-center shrink-0"><Check className="w-4 h-4 text-gold" /></div>
                      <span className="font-body text-sm text-navy">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Itinerary */}
            {pkg.itinerary?.length > 0 && (
              <div className="mb-10">
                <h3 className="font-heading text-xl font-semibold text-navy mb-6">Day-by-Day Itinerary</h3>
                <div className="space-y-4">
                  {pkg.itinerary.map((day, i) => (
                    <div key={i} data-testid={`itinerary-day-${day.day}`} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-10 h-10 bg-gold text-white rounded-full flex items-center justify-center font-body text-sm font-bold shrink-0">{day.day}</div>
                        {i < pkg.itinerary.length - 1 && <div className="w-0.5 flex-1 bg-gold/20 mt-2" />}
                      </div>
                      <div className="pb-6">
                        <h4 className="font-heading text-base font-semibold text-navy">{day.title}</h4>
                        <p className="font-body text-sm text-gray-500 mt-1">{day.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Inclusions / Exclusions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {pkg.inclusions?.length > 0 && (
                <div className="p-6 rounded-3xl bg-green-50 border border-green-100">
                  <h4 className="font-heading text-base font-semibold text-green-800 mb-4">Inclusions</h4>
                  <ul className="space-y-2">
                    {pkg.inclusions.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 font-body text-sm text-green-700"><Check className="w-4 h-4 text-green-500 shrink-0" />{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {pkg.exclusions?.length > 0 && (
                <div className="p-6 rounded-3xl bg-red-50 border border-red-100">
                  <h4 className="font-heading text-base font-semibold text-red-800 mb-4">Exclusions</h4>
                  <ul className="space-y-2">
                    {pkg.exclusions.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 font-body text-sm text-red-700"><X className="w-4 h-4 text-red-400 shrink-0" />{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Right - Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <div className="p-6 rounded-3xl bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="mb-4">
                  <span className="font-body text-xs text-gray-400">Starting from</span>
                  <p className="font-heading text-3xl font-bold text-gold">Rs.{pkg.price.toLocaleString('en-IN')}</p>
                  <span className="font-body text-xs text-gray-400">per person</span>
                </div>

                <div className="space-y-3">
                  <button onClick={() => addToCart(pkg.id)} data-testid="pkg-add-to-cart"
                    className="w-full bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_14px_0_rgba(197,160,89,0.39)]">
                    Add to Cart
                  </button>
                  <a href={`https://wa.me/919179992213?text=${encodeURIComponent(`Hi! I'm interested in the ${pkg.name} package (${pkg.destination}, ${pkg.duration}) at Rs.${pkg.price.toLocaleString('en-IN')}. Please share more details!`)}`}
                    target="_blank" rel="noopener noreferrer" data-testid="pkg-whatsapp-book"
                    className="flex items-center justify-center gap-2 w-full bg-green-500 hover:bg-green-600 text-white rounded-full py-3.5 font-body font-semibold transition-all">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Book via WhatsApp
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100 space-y-3">
                  <div className="flex items-center gap-3 font-body text-sm text-gray-500"><Check className="w-4 h-4 text-gold" />Flights not included (Domestic & International)</div>
                  <div className="flex items-center gap-3 font-body text-sm text-gray-500"><Check className="w-4 h-4 text-gold" />Non-refundable / partial-refund policy applies</div>
                  <div className="flex items-center gap-3 font-body text-sm text-gray-500"><Check className="w-4 h-4 text-green-500" />Instant confirmation</div>
                  <div className="flex items-center gap-3 font-body text-sm text-gray-500"><Check className="w-4 h-4 text-green-500" />24/7 customer support</div>
                </div>
              </div>

              <Link to="/explore" className="flex items-center gap-2 font-body text-sm text-gold hover:underline">
                <ArrowLeft className="w-4 h-4" /> Back to Packages
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
